import { useState, useCallback } from 'react';
import { BACKEND_URL } from '../config/appConfig';
import { toast } from 'sonner';

/**
 * usePostMealFlow — Manages post-log metabolic insights, portion editing,
 * food contribution, and post-meal completion sheet state.
 * Extracted from App.js Phase 5 Heavy Batch Round 2.
 */
export default function usePostMealFlow({ userId, isPremium, loadTodayMeals }) {
  // ── Post-Log Insight State ─────────────────────────────────────────────────
  const [showPostLogInsight, setShowPostLogInsight] = useState(false);
  const [postLogInsight, setPostLogInsight] = useState(null);
  const [lastLoggedMeal, setLastLoggedMeal] = useState(null);

  // ── Post-Meal Completion Sheet ─────────────────────────────────────────────
  const [showFoodContribution, setShowFoodContribution] = useState(false);
  const [showPostMealComplete, setShowPostMealComplete] = useState(false);
  const [postMealData, setPostMealData] = useState(null);
  const [foodContribution, setFoodContribution] = useState({
    foodName: '',
    description: '',
    calories: '',
    protein: '',
    carbs: '',
    fat: '',
    servingSize: ''
  });

  // ── Edit Portion Modal ─────────────────────────────────────────────────────
  const [showEditPortion, setShowEditPortion] = useState(false);
  const [editingMeal, setEditingMeal] = useState(null);
  const [portionPercent, setPortionPercent] = useState(100);
  const [portionLoading, setPortionLoading] = useState(false);

  // ── Fetch Post-Log Metabolic Insight ───────────────────────────────────────
  const fetchPostLogInsight = useCallback(async (mealData, mealType = 'Other') => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/meals/metabolic-insight`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          meal: mealData,
          meal_type: mealType,
          is_premium: isPremium
        })
      });

      if (response.ok) {
        const insight = await response.json();
        setPostLogInsight(insight);
        setLastLoggedMeal(mealData);
        return insight;
      }
    } catch (error) {
      console.error('Error fetching post-log insight:', error);
    }
    return null;
  }, [userId, isPremium]);

  // ── Dismiss Post-Log Insight ───────────────────────────────────────────────
  const dismissPostLogInsight = useCallback(() => {
    setShowPostLogInsight(false);
    setPostLogInsight(null);
    setLastLoggedMeal(null);
  }, []);

  // ── Save Portion Edit ──────────────────────────────────────────────────────
  const savePortionEdit = useCallback(async () => {
    if (!editingMeal) return;

    setPortionLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/meals/${editingMeal.meal_id}/portion`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          portion_percent: portionPercent,
          portion_source: 'manual'
        })
      });

      if (response.ok) {
        await loadTodayMeals(userId);
        setShowEditPortion(false);
        setEditingMeal(null);
        toast.success(`Portion updated to ${portionPercent}%`);
      } else {
        throw new Error('Failed to update portion');
      }
    } catch (error) {
      console.error('Error updating portion:', error);
      toast.error('Failed to update portion. Please try again.');
    } finally {
      setPortionLoading(false);
    }
  }, [editingMeal, portionPercent, userId, loadTodayMeals]);

  return {
    showPostLogInsight, setShowPostLogInsight,
    postLogInsight, setPostLogInsight,
    lastLoggedMeal, setLastLoggedMeal,
    showFoodContribution, setShowFoodContribution,
    showPostMealComplete, setShowPostMealComplete,
    postMealData, setPostMealData,
    foodContribution, setFoodContribution,
    showEditPortion, setShowEditPortion,
    editingMeal, setEditingMeal,
    portionPercent, setPortionPercent,
    portionLoading,
    fetchPostLogInsight,
    dismissPostLogInsight,
    savePortionEdit
  };
}
